import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/** Emplacements créés par l'app, dont le nom est le type lui-même. */
const KNOWN_TYPES = [
  'GARDEN',
  'GREENHOUSE',
  'PLANTER_BOX',
  'INDOOR',
  'TERRACE',
  'BALCONY',
];

/**
 * Rend lisible un nom d'emplacement.
 *
 * Quand l'utilisateur n'a pas nommé sa zone, l'app y écrit le TYPE brut :
 * l'écran affichait alors « PLANTER_BOX » et « INDOOR » en capitales, au
 * milieu de phrases en français. Le catalogue `common.location-types` existait
 * déjà pour l'onboarding et le profil ; il sert enfin partout.
 *
 * Un nom choisi par l'utilisateur (« Serre du fond ») traverse le pipe sans
 * être touché : on ne traduit que ce qui est manifestement une valeur d'enum.
 *
 * Plusieurs emplacements peuvent arriver séparés par « · » — le serveur les
 * regroupe quand une même culture pousse à plusieurs endroits.
 */
@Pipe({ name: 'locationLabel', standalone: true, pure: false })
export class LocationLabelPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(value: string | null | undefined): string {
    if (!value) return '';
    return value
      .split('·')
      .map((part) => this.label(part.trim()))
      .join(' · ');
  }

  private label(name: string): string {
    if (!KNOWN_TYPES.includes(name)) return name;
    const key = `common.location-types.${name}`;
    const translated = this.translate.instant(key);
    // `instant` renvoie la clé quand la traduction manque : mieux vaut alors
    // le nom d'origine qu'un chemin de clé affiché à l'écran.
    return translated === key ? name : translated;
  }
}
